import React, { useState, useEffect } from 'react';
import {
  ChevronRight,
  ShoppingCart,
  Check,
  Heart,
  Plus,
  Minus,
  ArrowLeft,
  Share2,
  ShieldCheck,
  Truck,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { Product } from '../types';

interface ProductDetailPageProps {
  slug: string; // e.g. "84051_N1" or "84051"
  initialProduct?: Product | null;
  onNavigateHome: () => void;
  onNavigateCatalog: () => void;
  onAddToCart: (product: Product, quantity: number) => void;
}

export const ProductDetailPage: React.FC<ProductDetailPageProps> = ({
  slug,
  initialProduct,
  onNavigateHome,
  onNavigateCatalog,
  onAddToCart,
}) => {
  const code = slug.replace(/_N[0-9]+$/i, '').trim();

  const [product, setProduct] = useState<Product | null>(initialProduct || null);
  const [activeImage, setActiveImage] = useState<string>(
    initialProduct?.image || `https://www.giffarine.com/image/goods/${code}-N1_01.jpg`
  );
  const [quantity, setQuantity] = useState<number>(1);
  const [isAdded, setIsAdded] = useState<boolean>(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(() => {
    try {
      const favs = JSON.parse(localStorage.getItem('giffarine_favorites') || '[]');
      return Array.isArray(favs) && favs.includes(code);
    } catch {
      return false;
    }
  });
  const [isLoading, setIsLoading] = useState<boolean>(!initialProduct?.fullDescription);
  const [copiedLink, setCopiedLink] = useState<boolean>(false);

  // Fetch full details and all images
  useEffect(() => {
    let isMounted = true;

    async function loadDetails() {
      try {
        const res = await fetch(`/api/product-detail/${slug}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.product && isMounted) {
            setProduct(data.product);
            if (data.product.images && data.product.images.length > 0) {
              setActiveImage(data.product.images[0]);
            } else if (data.product.image) {
              setActiveImage(data.product.image);
            }
          }
        }
      } catch (err) {
        console.error('[ProductDetailPage] Failed to fetch product details:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadDetails();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  const handleAddToCart = () => {
    if (!product) return;
    onAddToCart(product, quantity);
    setIsAdded(true);
    setTimeout(() => {
      setIsAdded(false);
    }, 1500);
  };

  const toggleFavorite = () => {
    const next = !isFavorite;
    setIsFavorite(next);
    try {
      const favs = JSON.parse(localStorage.getItem('giffarine_favorites') || '[]');
      const updated = next
        ? Array.from(new Set([...favs, code]))
        : favs.filter((c: string) => c !== code);
      localStorage.setItem('giffarine_favorites', JSON.stringify(updated));
    } catch (e) {
      console.error(e);
    }
  };

  const handleShare = () => {
    navigator.clipboard?.writeText(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const galleryImages =
    product?.images && product.images.length > 0
      ? product.images
      : [product?.image || `https://www.giffarine.com/image/goods/${code}-N1_01.jpg`];

  const price = product?.price || 0;
  const memberPrice = product?.memberPrice || price;
  const pv = product?.pv || 0;

  return (
    <div className="min-h-screen bg-slate-50/50 pb-16">
      {/* Top Breadcrumb Navigation */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
            <button
              onClick={onNavigateHome}
              className="hover:text-blue-600 transition-colors cursor-pointer shrink-0"
            >
              หน้าแรก
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <button
              onClick={onNavigateCatalog}
              className="hover:text-blue-600 transition-colors cursor-pointer shrink-0"
            >
              สินค้าทั้งหมด
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-slate-900 font-semibold truncate shrink-0 max-w-[200px] sm:max-w-md">
              {product?.name || `สินค้า รหัส ${code}`}
            </span>
          </nav>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 space-y-8">
        {/* Main Product Section: Two Columns (Images Left, Purchase Right) */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-5 sm:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left Column: Product Image Gallery */}
            <div className="space-y-4">
              {/* Primary Image View */}
              <div className="relative aspect-square w-full rounded-2xl bg-[#fafafa] border border-slate-200/80 p-6 flex items-center justify-center overflow-hidden">
                <img
                  src={activeImage}
                  alt={product?.name || code}
                  className="w-full h-full object-contain transition-all duration-300 hover:scale-105"
                  width="500"
                  height="500"
                  loading="eager"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = 'https://www.giffarine.com/image/goods/' + code + '-N1_01.jpg';
                  }}
                />

                {/* Authenticity Badge */}
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold shadow-xs">
                  <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                  <span>ของแท้ 100% จากศูนย์กิฟฟารีน</span>
                </div>
              </div>

              {/* Gallery Thumbnails (All images pulled from Giffarine) */}
              {galleryImages.length > 1 && (
                <div className="flex items-center gap-3 overflow-x-auto pb-2 pt-1 scrollbar-thin">
                  {galleryImages.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImage(imgUrl)}
                      className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-[#fafafa] border-2 p-1 overflow-hidden shrink-0 transition-all cursor-pointer ${
                        activeImage === imgUrl
                          ? 'border-blue-600 ring-2 ring-blue-100 shadow-xs'
                          : 'border-slate-200 hover:border-slate-400 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={imgUrl}
                        alt={`ภาพที่ ${idx + 1}`}
                        className="w-full h-full object-contain"
                        width="80"
                        height="80"
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column: Title, Code, Price, Quantity & Purchase Controls */}
            <div className="flex flex-col justify-between space-y-6">
              <div className="space-y-3">
                {/* Product Title */}
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-snug tracking-tight">
                  {product?.name || `กิฟฟารีน รหัส ${code}`}
                </h1>

                {/* Product Code */}
                <p className="text-sm font-medium text-slate-500">
                  รหัส {code}
                </p>

                {/* Price Display */}
                <div className="pt-2 pb-1">
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs sm:text-sm font-semibold text-slate-500">
                      ราคาเต็ม
                    </span>
                    <span className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                      {price.toLocaleString('th-TH', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                    <span className="text-sm font-medium text-slate-500">บาท</span>
                  </div>

                  {/* Member Price & PV Badge */}
                  {memberPrice > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-2.5">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-rose-50 border border-rose-200 text-[#E11D48] text-xs font-bold">
                        ราคาสมาชิก ฿{memberPrice.toLocaleString()}
                      </span>
                      {pv > 0 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold">
                          คะแนนสะสม {pv.toLocaleString()} PV
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-sm font-semibold text-slate-700">
                    จำนวน
                  </label>
                  <div className="flex items-center border border-slate-300 rounded-lg bg-white overflow-hidden shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-40 transition-colors"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v) && v >= 1) setQuantity(v);
                      }}
                      className="w-14 h-9 text-center text-sm font-bold text-slate-800 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Action Buttons Row (หยิบใส่ตะกร้า + เลือกสินค้าต่อ) */}
                <div className="pt-2 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={isAdded}
                    className={`px-8 py-3 rounded-lg text-sm sm:text-base font-semibold flex items-center justify-center gap-2 shadow-sm transition-all active:scale-[0.98] cursor-pointer ${
                      isAdded
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] hover:opacity-95 text-white shadow-blue-500/20'
                    }`}
                  >
                    {isAdded ? (
                      <>
                        <Check className="w-4 h-4" />
                        <span>หยิบใส่ตะกร้าแล้ว</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-4 h-4" />
                        <span>หยิบใส่ตะกร้า</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={onNavigateCatalog}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer"
                  >
                    เลือกสินค้าต่อ
                  </button>
                </div>

                {/* Favorite & Share */}
                <div className="pt-4 flex items-center gap-6 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={toggleFavorite}
                    className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors cursor-pointer ${
                      isFavorite
                        ? 'text-[#E60067]'
                        : 'text-slate-600 hover:text-[#E60067]'
                    }`}
                  >
                    <Heart
                      className={`w-4 h-4 ${
                        isFavorite ? 'fill-[#E60067] text-[#E60067]' : 'text-slate-400'
                      }`}
                    />
                    <span>{isFavorite ? 'อยู่ในรายการโปรด' : 'รายการโปรด'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    <Share2 className="w-4 h-4 text-slate-400" />
                    <span>{copiedLink ? 'คัดลอกลิงก์แล้ว!' : 'แชร์สินค้านี้'}</span>
                  </button>
                </div>
              </div>

              {/* Assurances Bar */}
              <div className="grid grid-cols-2 gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-200/60 text-xs text-slate-600 font-medium">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>จัดส่งด่วนทั่วประเทศ</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#E60067] shrink-0" />
                  <span>สะสมคะแนน PV เต็มจำนวน</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Details Card: Exact layout matching Image 2 */}
        <div className="bg-[#f8f9fa] rounded-2xl border border-slate-200/90 shadow-sm p-6 sm:p-10 space-y-6">
          {/* Section 1: รายละเอียด (Description & Bullet Points) */}
          <div className="space-y-4">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
              รายละเอียด
            </h2>

            {isLoading ? (
              <div className="space-y-2 py-4 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                <div className="h-4 bg-slate-200 rounded w-full"></div>
                <div className="h-4 bg-slate-200 rounded w-5/6"></div>
              </div>
            ) : (
              <div className="text-sm sm:text-base text-slate-700 leading-relaxed whitespace-pre-line space-y-3 font-normal">
                {product?.fullDescription || product?.description || 'ไม่มีข้อมูลรายละเอียดเพิ่มเติม'}
              </div>
            )}

            {/* Specifications (อย., ปริมาณบรรจุ, น้ำหนักรวม, จำนวน) */}
            <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {product?.fda && (
                <div className="space-y-1">
                  <h3 className="font-semibold text-slate-900">
                    เลขที่ อย. / จดแจ้ง:
                  </h3>
                  <p className="text-slate-600 font-mono">{product.fda}</p>
                </div>
              )}

              {product?.volume && (
                <div className="space-y-1">
                  <h3 className="font-semibold text-slate-900">
                    ปริมาณบรรจุ:
                  </h3>
                  <p className="text-slate-600">{product.volume}</p>
                </div>
              )}

              {product?.weight && (
                <div className="space-y-1">
                  <h3 className="font-semibold text-slate-900">
                    น้ำหนักรวม:
                  </h3>
                  <p className="text-slate-600">{product.weight}</p>
                </div>
              )}

              <div className="space-y-1">
                <h3 className="font-semibold text-slate-900">
                  จำนวน:
                </h3>
                <p className="text-slate-600">{product?.quantityUnit || '1 ชิ้น'}</p>
              </div>
            </div>
          </div>

          {/* Divider Line */}
          <hr className="border-slate-200 my-6" />

          {/* Section 2: วิธีใช้ (How to use) */}
          <div className="space-y-3">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
              วิธีใช้
            </h2>
            <div className="text-sm sm:text-base text-slate-700 leading-relaxed whitespace-pre-line font-normal">
              {product?.howToUse ||
                'ทาหรือรับประทานตามคำแนะนำบนฉลากผลิตภัณฑ์ หรือปรึกษาผู้เชี่ยวชาญกิฟฟารีน'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
